<template>
  <div>
    <media-uploader
      ref="mediaUploader"
      v-slot:default="slotProps"
      @uploads="updateUploads"
      @valid="emitValid"
      @saving="handleSaving"
    >
    </media-uploader>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref } from "vue"
import MediaUploader, {
  MediaUploaderType,
} from "features/media/MediaUploader.vue"
import Header from "../../components/UI/Header.vue"
import { AttachmentMediaUpload } from "features/attachments/attachment.types"
import { MediaUpload } from "features/media/media.types"
import AttachmentUploadTableItem from "features/attachments/AttachmentUploadTableItem.vue"
import { v4 as uuidv4 } from "uuid"

export default defineComponent({
  name: "AttachmentUploader",
  components: {
    MediaUploader,
    Header,
    AttachmentUploadTableItem,
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile
    },
  },
  data() {
    return {
      attachmentUploads: [] as Ref<AttachmentMediaUpload>[],
    }
  },
  methods: {
    handleSaving(isSaving: boolean) {
      this.$emit("saving", isSaving)
    },
    updateUploads(updatedUploads: MediaUpload[]): void {
      updatedUploads.forEach((updatedUpload) => {
        const oldUpload = this.attachmentUploads.find(
          (u) => u.value.file.token === updatedUpload.file.token
        )
        if (!oldUpload) {
          console.warn(
            "Updating an non-existent upload!!",
            updatedUpload,
            this.attachmentUploads
          )
        }
        oldUpload.value = {
          ...oldUpload.value,
          ...updatedUpload,
        }
      })
    },
    emitValid(valid: boolean) {
      this.$emit("valid", valid)
    },
    retryUpload(token: string) {
      if (token) {
        const mediaUploader = this.$refs.mediaUploader as MediaUploaderType
        mediaUploader.retry(token)
      }
    },
    manuallyAddFileAndUpload(file: File): Ref<AttachmentMediaUpload> {
      const mediaUploader = this.$refs.mediaUploader as MediaUploaderType
      const { storedFile, upload } =
        mediaUploader.manuallyAddFileAndUpload(file)

      let fileNameComponents = file.name?.split(".")
      const ext =
        fileNameComponents.length > 1 ? fileNameComponents.pop() : "FILE"
      const fileName = fileNameComponents.join()
      const attachmentMediaUpload = ref({
        frontendId: uuidv4(),
        file: upload,
        name: fileName,
        type: ext,
        storedFile,
        uploadProgress: 0,
      } as AttachmentMediaUpload)
      this.attachmentUploads.push(attachmentMediaUpload)
      return attachmentMediaUpload
    },
    manuallyOpenFileBrowser() {
      const mediaUploader = this.$refs.mediaUploader as MediaUploaderType
      mediaUploader.manuallyOpenFileBrowser()
    },
    manuallyDeleteUpload(frontendId: string) {
      const attachment = this.attachmentUploads.find(
        (upload) => upload.value.frontendId === frontendId
      )

      // Will emit the uploads once more including the cancelled upload with cancelled state
      // Therefore trigger it first and afterwards delete from list
      const mediaUploader = this.$refs.mediaUploader as MediaUploaderType
      mediaUploader.manuallyDeleteUpload(attachment.value.file.token)

      console.log("Attachment found for cancellation", attachment)
      this.attachmentUploads = this.attachmentUploads.filter(
        (upload) => upload.value.frontendId !== frontendId
      )
    },
    manuallyReset() {
      this.attachmentUploads = []
      const mediaUploader = this.$refs.mediaUploader as MediaUploaderType
      mediaUploader.manuallyReset()
    },
  },
})
</script>

<style lang="sass" scoped>
.attachment-uploads-grid
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr))
  grid-gap: 16px

.full-width
  width: 100%
</style>
